import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ref, onValue, update, remove } from 'firebase/database';
import { database } from '../../firebase';

const Dashboard = () => {
  const { employeeContext, userData } = useAuth();
  const { companyId, employeeId } = employeeContext || {};
  
  const [ptoBalance, setPtoBalance] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [nextReview, setNextReview] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [bgChoice, setBgChoice] = useState('bg-one.jpg');
  const [loading, setLoading] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Derive Display Name
  const displayName = userData?.profile?.name || userData?.name || 'Employee';
  const firstName = displayName.split(' ')[0];

  useEffect(() => {
    if (!companyId || !employeeId) return;

    // Fetch PTO Balance
    const ptoRef = ref(database, `companies/${companyId}/employees/${employeeId}/pto`);
    onValue(ptoRef, (snap) => {
      const data = snap.val() || {};
      setPtoBalance(data.balanceDays || 0);
    });

    // Fetch Next Review Date from Performance
    const perfRef = ref(database, `companies/${companyId}/employees/${employeeId}/performance`);
    onValue(perfRef, (snap) => {
      const data = snap.val() || {};
      if (data.nextReviewDate) {
        setNextReview(data.nextReviewDate);
      } else {
        setNextReview(null);
      }
    });

    // Fetch Notifications
    const notifRef = ref(database, `companies/${companyId}/employees/${employeeId}/notifications`);
    onValue(notifRef, (snap) => {
      const data = snap.val() || {};
      const notifsList = Object.entries(data).map(([id, notif]) => ({
        id,
        ...notif
      })).sort((a, b) => b.timestamp - a.timestamp);
      
      // For demo purposes, if no notifications exist, show example ones
      // if (notifsList.length === 0) {
      //   setNotifications([]);
      // } else {
        setNotifications(notifsList);
      // }
    });

    // Fetch Onboarding Tasks
    const tasksRef = ref(database, `companies/${companyId}/onboarding/${employeeId}/tasks`);
    onValue(tasksRef, (snap) => {
      const data = snap.val() || {};
      let count = 0;
      Object.values(data).forEach(category => {
        Object.values(category).forEach(task => {
          if (task.status !== 'completed') count++;
        });
      });
      setPendingTasks(count);
    });

    // Fetch Preferences
    const prefRef = ref(database, `companies/${companyId}/employees/${employeeId}/preferences`);
    onValue(prefRef, (snap) => {
        const prefs = snap.val() || {};
        if (prefs.dashboardBg) {
            setBgChoice(prefs.dashboardBg);
        }
        setLoading(false);
    });

  }, [companyId, employeeId]);

  // Preload background image
  useEffect(() => {
    const img = new Image();
    img.src = `/${bgChoice}`;
    img.onload = () => setImageLoaded(true);
  }, [bgChoice]);

  const handleBgToggle = (direction) => {
    const currentIndex = bgOptions.findIndex(bg => bg.id === bgChoice);
    let newIndex;
    if (direction === 'next') {
        newIndex = (currentIndex + 1) % bgOptions.length;
    } else {
        newIndex = (currentIndex - 1 + bgOptions.length) % bgOptions.length;
    }
    const newBg = bgOptions[newIndex].id;
    setBgChoice(newBg);
    
    // Auto-save preference
    if (companyId && employeeId) {
        update(ref(database, `companies/${companyId}/employees/${employeeId}/preferences`), {
            dashboardBg: newBg
        });
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Available backgrounds
  const bgOptions = [
    { id: 'bg-one.jpg', name: 'Studio', preview: '/bg-one.jpg' },
    { id: 'bg-two.jpg', name: 'City', preview: '/bg-two.jpg' },
    { id: 'bg-three.jpg', name: 'Desk', preview: '/bg-three.jpg' },
    { id: 'bg-four.jpg', name: 'Window', preview: '/bg-four.jpg' },
    { id: 'bg-five.jpg', name: 'Work', preview: '/bg-five.jpg' },
  ];

  // Determine current background style
  const bannerStyle = {
    backgroundImage: `url(/${bgChoice})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  // Background color for the callout box (matching stat cards)
  const calloutBgColor = 'rgba(86, 141, 237, 0.08)';

  if (loading || !imageLoaded) {
    return (
        <div style={{ 
            height: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#FFFFFF',
            fontFamily: 'Montserrat'
        }}>
            <div style={{ textAlign: 'center' }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: '32px', marginBottom: '16px', color: '#568ded' }}></i>
                <div>Loading your dashboard...</div>
            </div>
        </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Welcome Banner */}
      <div className="welcome-banner" style={bannerStyle}>
        <div className="welcome-content">
            <h1>{getGreeting()}, {firstName}</h1>
            <p>Welcome to your employee portal. Here's what's happening today.</p>
        </div>
        
        <div className="banner-controls">
            <div className="date-display">
                <i className="fa-regular fa-calendar"></i>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
            <div className="bg-toggle-controls">
                <button 
                    className="bg-toggle-btn" 
                    onClick={() => handleBgToggle('prev')}
                    title="Previous Background"
                >
                    <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button 
                    className="bg-toggle-btn" 
                    onClick={() => handleBgToggle('next')}
                    title="Next Background"
                >
                    <i className="fa-solid fa-chevron-right"></i>
                </button>
            </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card task-card">
          <div className="stat-icon">
            <i className="fa-solid fa-clipboard-list"></i>
          </div>
          <div className="stat-info">
            <span className="stat-label">Pending Tasks</span>
            <div className="stat-value">{pendingTasks}</div>
            <div className="stat-sub">{pendingTasks > 0 ? 'Action items remaining' : 'All caught up!'}</div>
          </div>
        </div>

        <div className="stat-card pto-card">
          <div className="stat-icon">
            <i className="fa-solid fa-umbrella-beach"></i>
          </div>
          <div className="stat-info">
            <span className="stat-label">PTO Balance</span>
            <div className="stat-value">{ptoBalance} <span style={{fontSize: '16px', fontWeight: 'normal'}}>Days</span></div>
            <div className="stat-sub">Available for use</div>
          </div>
        </div>

        <div className="stat-card upcoming-card">
          <div className="stat-icon">
            <i className="fa-solid fa-chart-line"></i>
          </div>
          <div className="stat-info">
            <span className="stat-label">Next Review</span>
            <div className="stat-value text-small">
                {nextReview ? new Date(nextReview).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'}) : 'Not Scheduled'}
            </div>
            <div className="stat-sub">
                Performance Check-in
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="notifications-section" style={{ marginBottom: '40px' }}>
        <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
          <span style={{ flex: 1 }}>Notifications</span>
          {notifications.length > 0 && (
              <button 
                  onClick={() => {
                      // Clear all notifications
                      if (window.confirm('Clear all notifications?')) {
                           update(ref(database, `companies/${companyId}/employees/${employeeId}`), {
                              notifications: null
                          });
                      }
                  }}
                  style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '12px',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      padding: 0
                  }}
              >
                  Clear All
              </button>
          )}
        </h2>
        
        {notifications.length === 0 ? (
            <div style={{ 
                padding: '40px', 
                textAlign: 'center', 
                background: 'rgba(255,255,255,0.02)', 
                borderRadius: '12px',
                border: '1px dashed rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.4)'
            }}>
                <i className="fa-regular fa-bell" style={{ fontSize: '24px', marginBottom: '12px', display: 'block', opacity: 0.7 }}></i>
                <div style={{ fontSize: '14px' }}>You're all caught up! No new notifications.</div>
            </div>
        ) : (
            <div className="notifications-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {notifications.map(notif => (
                <div key={notif.id} style={{
                  backgroundColor: 'rgba(86, 141, 237, 0.08)',
                  border: '1px solid rgba(86, 141, 237, 0.15)',
                  borderLeft: `4px solid ${notif.type === 'error' ? '#EB4040' : notif.type === 'success' ? '#40EB6E' : '#568ded'}`,
                  borderRadius: '8px',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start'
                }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '4px', fontSize: '15px' }}>{notif.title}</div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', lineHeight: '1.4' }}>{notif.message}</div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '8px' }}>
                      {new Date(notif.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <button 
                    onClick={() => {
                      remove(ref(database, `companies/${companyId}/employees/${employeeId}/notifications/${notif.id}`));
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.4)',
                      cursor: 'pointer',
                      padding: '4px'
                    }}
                    title="Dismiss"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
        )}
      </div>

      {/* Quick Actions & Recent */}
      <div className="dashboard-layout">
        <div className="main-section">
            <h2 className="section-title">Quick Actions</h2>
            <div className="actions-grid">
                <a href="/time-off" className="action-card">
                    <div className="action-icon action-blue">
                        <i className="fa-solid fa-plus"></i>
                    </div>
                    <h3>Request Time Off</h3>
                    <p>Schedule your next vacation or leave.</p>
                </a>
                
                <a href="/onboarding" className="action-card">
                    <div className="action-icon action-green">
                        <i className="fa-solid fa-check-double"></i>
                    </div>
                    <h3>Complete Tasks</h3>
                    <p>View and finish your assigned onboarding tasks.</p>
                </a>

                <a href="/documents" className="action-card">
                    <div className="action-icon action-purple">
                        <i className="fa-solid fa-file-arrow-up"></i>
                    </div>
                    <h3>Upload Documents</h3>
                    <p>Submit required forms and ID documents.</p>
                </a>

                <a href="/profile" className="action-card">
                    <div className="action-icon action-orange">
                        <i className="fa-solid fa-user-pen"></i>
                    </div>
                    <h3>Update Profile</h3>
                    <p>Keep your personal information up to date.</p>
                </a>
            </div>
        </div>
      </div>

      <style>{`
        .dashboard-container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 20px;
        }

        /* Welcome Banner */
        .welcome-banner {
            border-radius: 24px;
            padding: 50px;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            align-items: flex-start;
            margin-bottom: 40px;
            box-shadow: 0 20px 40px rgba(0,0,0,0.2);
            border: 1px solid rgba(255,255,255,0.1);
            position: relative;
            min-height: 380px;
            transition: background-image 0.5s ease-in-out;
            overflow: hidden;
        }
        
        /* Gradient Overlay - fixed to cover entire banner including rounded corners */
        .welcome-banner::before {
            content: '';
            position: absolute;
            top: -1px;
            left: -1px;
            right: -1px;
            bottom: -1px;
            background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.75) 100%);
            z-index: 1;
            border-radius: 24px;
            pointer-events: none;
        }
        
        .welcome-content {
            position: relative;
            z-index: 2;
            max-width: 65%;
            padding-bottom: 10px;
        }
        
        .welcome-content h1 {
            font-size: 48px;
            margin: 0 0 12px 0;
            font-family: 'Montserrat', sans-serif;
            font-weight: 800;
            color: #FFFFFF;
            letter-spacing: -1px;
            line-height: 1.1;
            text-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }
        .welcome-content p {
            color: rgba(255,255,255,0.95);
            margin: 0;
            font-size: 18px;
            font-weight: 500;
            text-shadow: 0 2px 4px rgba(0,0,0,0.3);
        }
        
        .banner-controls {
            position: absolute;
            bottom: 40px;
            right: 40px;
            z-index: 2;
            display: flex;
            align-items: center;
            gap: 16px;
        }
        
        .bg-toggle-controls {
            display: flex;
            gap: 8px;
            background: rgba(255,255,255,0.1);
            padding: 6px;
            border-radius: 30px;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.15);
        }
        
        .bg-toggle-btn {
            background: transparent;
            border: none;
            color: white;
            width: 36px;
            height: 36px;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
        }
        .bg-toggle-btn:hover {
            background: rgba(255,255,255,0.2);
            transform: scale(1.05);
        }

        .date-display {
            background: rgba(0,0,0,0.4);
            padding: 10px 20px;
            border-radius: 30px;
            font-size: 14px;
            font-weight: 600;
            backdrop-filter: blur(10px);
            border: 1px solid rgba(255,255,255,0.15);
            color: white;
            display: flex;
            align-items: center;
            gap: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        
        .bg-picker-popup {
            position: absolute;
            bottom: 80px;
            right: 40px;
            background: rgba(10, 25, 47, 0.95);
            padding: 12px;
            border-radius: 12px;
            display: flex;
            gap: 10px;
            z-index: 10;
            border: 1px solid rgba(255,255,255,0.15);
            box-shadow: 0 4px 20px rgba(0,0,0,0.5);
        }
        .bg-option {
            width: 50px;
            height: 50px;
            border-radius: 8px;
            cursor: pointer;
            border: 2px solid rgba(255,255,255,0.1);
            transition: transform 0.2s;
            background-size: cover;
            background-position: center;
        }
        .bg-option:hover {
            transform: scale(1.1);
            border-color: white;
        }
        .bg-option.active {
            border-color: #568ded;
            box-shadow: 0 0 0 2px rgba(86, 141, 237, 0.3);
        }

        /* Tablet / Medium screens */
        @media (max-width: 1024px) {
            .welcome-banner {
                min-height: 320px;
                padding: 40px;
            }
            .welcome-content {
                max-width: 100%;
                padding-bottom: 70px;
            }
            .welcome-content h1 {
                font-size: 36px;
            }
            .welcome-content p {
                font-size: 16px;
            }
            .banner-controls {
                bottom: 30px;
                right: 30px;
            }
        }

        /* Stats Grid */
        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            margin-bottom: 40px;
        }
        .stat-card {
            background: rgba(86, 141, 237, 0.08);
            border: 1px solid rgba(86, 141, 237, 0.15);
            border-radius: 16px;
            padding: 24px;
            display: flex;
            align-items: center;
            gap: 20px;
            /* Removed hover transform/shadow as requested */
        }
        
        .stat-icon {
            width: 60px;
            height: 60px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            flex-shrink: 0;
        }
        .task-card .stat-icon { background: rgba(255, 187, 51, 0.15); color: #FFBB33; }
        .pto-card .stat-icon { background: rgba(0, 200, 81, 0.15); color: #00C851; }
        .upcoming-card .stat-icon { background: rgba(51, 181, 229, 0.15); color: #33b5e5; }

        .stat-info {
            flex: 1;
        }
        .stat-label {
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: rgba(255,255,255,0.6);
            display: block;
            margin-bottom: 4px;
        }
        .stat-value {
            font-size: 28px;
            font-weight: 700;
            margin-bottom: 4px;
            line-height: 1.2;
        }
        .stat-value.text-small {
            font-size: 24px;
        }
        .stat-sub {
            font-size: 13px;
            color: rgba(255,255,255,0.5);
        }

        /* Actions Grid */
        .section-title {
            font-size: 20px;
            margin-bottom: 20px;
            font-family: 'Montserrat', sans-serif;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .section-title::before {
            content: '';
            display: block;
            width: 4px;
            height: 24px;
            background: #568ded;
            border-radius: 2px;
        }

        .actions-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
            gap: 20px;
        }
        .action-card {
            background: rgba(255,255,255,0.03);
            border: 1px solid rgba(255,255,255,0.05);
            border-radius: 12px;
            padding: 24px;
            text-decoration: none;
            color: inherit;
            transition: all 0.2s;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }
        /* Keep hover effects for action cards */
        .action-card:hover {
            background: rgba(255,255,255,0.06);
            border-color: rgba(255,255,255,0.1);
            transform: translateY(-2px);
        }
        .action-icon {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            margin-bottom: 16px;
        }
        .action-blue { background: rgba(66, 133, 244, 0.1); color: #4285F4; }
        .action-green { background: rgba(52, 168, 83, 0.1); color: #34A853; }
        .action-purple { background: rgba(160, 100, 234, 0.1); color: #A064EA; }
        .action-orange { background: rgba(255, 140, 0, 0.1); color: #FF8C00; }

        .action-card h3 {
            font-size: 16px;
            margin: 0 0 8px 0;
            font-weight: 600;
        }
        .action-card p {
            font-size: 13px;
            color: rgba(255,255,255,0.5);
            margin: 0;
            line-height: 1.4;
        }

        @media (max-width: 768px) {
            .welcome-banner {
                min-height: 280px;
                padding: 24px;
                padding-bottom: 100px;
            }
            .welcome-content {
                max-width: 100%;
                padding-bottom: 0;
            }
            .welcome-content h1 {
                font-size: 28px;
                letter-spacing: -0.5px;
            }
            .welcome-content p {
                font-size: 14px;
            }
            .banner-controls {
                position: absolute;
                bottom: 20px;
                left: 24px;
                right: 24px;
                justify-content: space-between;
            }
            .date-display {
                padding: 8px 14px;
                font-size: 12px;
            }
            .bg-toggle-btn {
                width: 32px;
                height: 32px;
            }
            .stats-grid {
                grid-template-columns: 1fr;
            }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
